import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import type { TestResult } from '@/types/test'

interface TestResultPageProps {
  result: TestResult
  onRetake: () => void
}

export function TestResultPage({ result, onRetake }: TestResultPageProps) {
  const avgSeconds = result.avgAnswerMs ? (result.avgAnswerMs / 1000).toFixed(1) : null
  const durationMinutes = result.durationMs ? Math.max(1, Math.round(result.durationMs / 60000)) : null

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Card className="p-8 text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">Test complete</p>
        <h2 className="mt-3 text-4xl font-bold text-foreground">{Math.round(result.scorePercent)}%</h2>
        <p className="mt-2 text-muted-foreground">
          {result.correctAnswers} / {result.totalQuestions} correct
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
          {avgSeconds ? <span>Average time: {avgSeconds}s per question</span> : null}
          {durationMinutes ? <span>Duration: {durationMinutes} min</span> : null}
        </div>
      </Card>

      {result.weakWords.length > 0 ? (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-foreground">Weak words ({result.weakWords.length})</h3>
          <ul className="mt-4 space-y-3">
            {result.weakWords.map((weakWord) => (
              <li key={weakWord.userWordId} className="rounded-lg border border-border p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Link
                    to={`/vocabulary/${weakWord.userWordId}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {weakWord.word}
                  </Link>
                  <span className="text-sm text-muted-foreground">
                    You answered: {weakWord.yourAnswer || '—'}
                  </span>
                </div>
                <p className="mt-1 text-sm text-foreground">Correct: {weakWord.correctAnswer}</p>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <div className="flex flex-wrap gap-3">
        {result.weakWords.length > 0 ? (
          <Link
            to={`/review?userWordIds=${result.weakWords.map((weakWord) => weakWord.userWordId).join(',')}`}
            className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90"
            data-testid="review-weak-words-cta"
          >
            Review weak words
          </Link>
        ) : null}
        <Button className="w-auto bg-secondary text-secondary-foreground hover:bg-secondary/80" onClick={onRetake}>
          Take another test
        </Button>
        <Link
          to="/vocabulary"
          className="inline-flex h-10 items-center justify-center rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary"
        >
          Back to vocabulary
        </Link>
      </div>
    </div>
  )
}
